import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  Loader2,
  Download,
  Trash2,
  Mail,
  UserX,
  UserCheck,
  CheckCircle2,
  AlertCircle,
  Users,
  UserMinus,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPut, apiDelete } from '../lib/api'

interface Subscriber {
  id: string
  email: string
  name: string
  source: string
  isActive: boolean
  confirmedAt: string | null
  unsubscribedAt: string | null
  createdAt: string
}

interface NewsletterData {
  total: number
  page: number
  limit: number
  pages: number
  stats: {
    activos: number
    unsubscribed: number
  }
  data: Subscriber[]
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

export function Newsletter() {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [onlyActive, setOnlyActive] = useState(true)
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)

  function notify(tipo: 'ok' | 'err', msg: string) {
    setFlash({ tipo, msg })
    setTimeout(() => setFlash(null), 3000)
  }

  const queryUrl = (() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', '25')
    if (searchTerm) params.set('q', searchTerm)
    if (onlyActive) params.set('active', 'true')
    return `/api/admin/newsletter?${params.toString()}`
  })()

  const { data, isLoading, isFetching } = useQuery<NewsletterData>({
    queryKey: ['newsletter', page, searchTerm, onlyActive],
    queryFn: () => apiGet(queryUrl),
    placeholderData: (prev) => prev,
  })

  const toggleMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiPut<{ ok: true }>(`/api/admin/newsletter/${id}`, { is_active: isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['newsletter'] })
    },
    onError: (e: Error) => notify('err', e.message),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: true }>(`/api/admin/newsletter/${id}`),
    onSuccess: () => {
      notify('ok', 'Suscriptor eliminado')
      qc.invalidateQueries({ queryKey: ['newsletter'] })
    },
    onError: (e: Error) => notify('err', e.message),
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearchTerm(q.trim())
  }

  return (
    <>
      <Topbar
        title="Newsletter"
        subtitle={
          data
            ? `${data.stats.activos.toLocaleString('es-MX')} suscriptores activos · ${data.stats.unsubscribed.toLocaleString('es-MX')} desuscritos`
            : 'Cargando…'
        }
        actions={
          <a
            href="/api/admin/newsletter/export?format=csv"
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <Download className="size-3.5" /> Exportar CSV
          </a>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <AlertCircle className="size-4" />
          )}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {/* Stats */}
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-ink-200 bg-white p-5">
            <div className="flex items-start justify-between">
              <div className="text-sm font-medium text-ink-500">Activos</div>
              <div className="grid size-9 place-items-center rounded-lg bg-emerald-50 text-emerald-600">
                <Users className="size-4" />
              </div>
            </div>
            <div className="mt-3 text-2xl font-extrabold tracking-tight text-ink-900">
              {data ? data.stats.activos.toLocaleString('es-MX') : '—'}
            </div>
          </div>
          <div className="rounded-2xl border border-ink-200 bg-white p-5">
            <div className="flex items-start justify-between">
              <div className="text-sm font-medium text-ink-500">Desuscritos</div>
              <div className="grid size-9 place-items-center rounded-lg bg-amber-50 text-amber-600">
                <UserMinus className="size-4" />
              </div>
            </div>
            <div className="mt-3 text-2xl font-extrabold tracking-tight text-ink-900">
              {data ? data.stats.unsubscribed.toLocaleString('es-MX') : '—'}
            </div>
          </div>
          <div className="rounded-2xl border border-ink-200 bg-white p-5">
            <div className="flex items-start justify-between">
              <div className="text-sm font-medium text-ink-500">Total</div>
              <div className="grid size-9 place-items-center rounded-lg bg-blue-50 text-blue-600">
                <Mail className="size-4" />
              </div>
            </div>
            <div className="mt-3 text-2xl font-extrabold tracking-tight text-ink-900">
              {data
                ? (data.stats.activos + data.stats.unsubscribed).toLocaleString('es-MX')
                : '—'}
            </div>
          </div>
        </div>

        {/* Filtros */}
        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por email o nombre…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
          <label className="ml-2 inline-flex select-none items-center gap-2 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => {
                setOnlyActive(e.target.checked)
                setPage(1)
              }}
              className="size-4 accent-brand-600"
            />
            Solo activos
          </label>
        </form>

        {/* Tabla */}
        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando suscriptores…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Nombre</th>
                      <th className="px-4 py-3 font-medium">Origen</th>
                      <th className="px-4 py-3 font-medium">Alta</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="w-28 px-4 py-3 text-right font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((s) => (
                      <tr
                        key={s.id}
                        className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-4 py-3 font-medium text-ink-900">{s.email}</td>
                        <td className="px-4 py-3 text-ink-700">{s.name || '—'}</td>
                        <td className="px-4 py-3 text-xs text-ink-500">{s.source || '—'}</td>
                        <td className="px-4 py-3 text-xs text-ink-600">
                          {formatDate(s.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          {s.isActive ? (
                            <Badge variant="success">Activo</Badge>
                          ) : (
                            <Badge variant="warning">Desuscrito</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            {s.isActive ? (
                              <button
                                type="button"
                                title="Desuscribir"
                                onClick={() =>
                                  toggleMut.mutate({ id: s.id, isActive: false })
                                }
                                className="grid size-8 place-items-center rounded text-ink-500 hover:bg-amber-50 hover:text-amber-700"
                              >
                                <UserX className="size-4" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                title="Reactivar"
                                onClick={() =>
                                  toggleMut.mutate({ id: s.id, isActive: true })
                                }
                                className="grid size-8 place-items-center rounded text-ink-500 hover:bg-emerald-50 hover:text-emerald-700"
                              >
                                <UserCheck className="size-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              title="Eliminar"
                              onClick={() => {
                                if (confirm(`Eliminar ${s.email} de la base?`)) {
                                  deleteMut.mutate(s.id)
                                }
                              }}
                              className="grid size-8 place-items-center rounded text-red-500 hover:bg-red-50"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data.data.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-ink-500">
                          Sin suscriptores con esos filtros.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {data.pages > 1 && (
                <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                  <div className="text-ink-500">
                    Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                    <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong>{' '}
                    ({data.total.toLocaleString('es-MX')} resultados)
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button className="rounded-md bg-brand-600 px-3 py-1 text-white">
                      {page}
                    </button>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= data.pages}
                      className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
